// Lib
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { EditorContent } from '@tiptap/react';

// Utils
import { getElementId } from '../../../../common/elements/utils/elementPropertyUtils';
import { getMainEditorId, getMainEditorKey } from '../../utils/elementEditorUtils';
import { canWrite } from '../../../../common/permissions/permissionUtil';

// Types
import type { ImMNElement } from '../../../../common/elements/elementModelTypes';
import type { SaveTiptapEditorContentFunction } from '../../../../common/tiptap/extensions/externalStoreSync/externalStoreSynchroniserTypes';
import type { TiptapContent } from '../../../../common/tiptap/tiptapTypes';

// Hooks
import useTaskListEditorExtensions from './useTaskListEditorExtensions';
import useTiptapInnerRef from '../../../../common/tiptap/hooks/useTiptapInnerRef';
import usePoiUpdateHandler from '../../../components/pointsOfInterest/usePoiUpdateHandler';
import useClientElementTiptapEditor from '../../../components/tiptapEditor/useClientElementTiptapEditor';
import useTaskListFeatureSuggestionTriggers from '../suggestion/singleEditor/useTaskListFeatureSuggestionTriggers';

// Components
import { SearchHighlightObserver } from '../../../../common/tiptap/extensions/SearchHighlighter';
import TaskListDropTarget from './TaskListDropTarget';

// Styles
import './TaskListEditor.scss';

export type TaskListEditorProps = {
    element: ImMNElement;
    textContent: TiptapContent | null;
    saveContent: SaveTiptapEditorContentFunction;
    isEditable: boolean;
    isEditing: boolean;
    isSingleSelected: boolean;
    isFocusedForegroundElement?: boolean;
    currentEditorId?: string;
    startEditing: (args: { editorId: string; editorKey: string }) => void;
    spellCheck: boolean;
    permissions?: number;
    moveToTrashHandler?: (args: { elementId: string }) => unknown;
    setParentHoveredChildAcceptsDrop?: (hoveredChildAcceptsDrop: boolean) => void;
};

/**
 * Renders a Tiptap task list editor wired into Milanote's element editor lifecycle.
 */
const TaskListEditor: React.FC<TaskListEditorProps> = (props) => {
    const {
        element,
        textContent,
        saveContent,
        isEditable,
        isEditing,
        isSingleSelected,
        isFocusedForegroundElement,
        currentEditorId,
        startEditing,
        spellCheck,
        permissions,
        moveToTrashHandler,
        setParentHoveredChildAcceptsDrop,
    } = props;

    const elementId = getElementId(element);

    const { innerRef, contentInnerRef } = useTiptapInnerRef();
    const onHighlightsChanged = usePoiUpdateHandler({ contentInnerRef, elementId });

    const onEmptyBackspace = useCallback(() => {
        moveToTrashHandler?.({ elementId });
        return !!moveToTrashHandler;
    }, [elementId, moveToTrashHandler]);

    // Board write-permission is stable for the editor's lifetime (unlike `isEditable`,
    // which flips as the user enters/leaves editing), so it can be baked into the
    // extension config and read by the task item node views without a React context.
    const canModifyContent = permissions === undefined ? true : canWrite(permissions);

    const dispatch = useDispatch();

    const extensions = useTaskListEditorExtensions({
        saveContent,
        isEditable,
        dispatch,
        onHighlightsChanged,
        onEmptyBackspace,
        canModifyContent,
        elementId,
        isElementSingleSelected: isSingleSelected,
    });

    const editorIdArgs = { element, isFocusedForegroundElement };
    const editorId = getMainEditorId(editorIdArgs);
    const editorKey = getMainEditorKey(editorIdArgs);

    const { editor, onMouseDown, onClick } = useClientElementTiptapEditor({
        extensions,
        persistedContent: textContent,

        editorId,
        editorKey,
        currentEditorId,

        isEditable,
        isEditing,
        isSingleSelected,

        startEditing,
        saveContent,
        registryEditorId: editorId,
    });

    useTaskListFeatureSuggestionTriggers({ editor, element });

    return (
        <TaskListDropTarget
            editor={editor}
            elementId={elementId}
            setParentHoveredChildAcceptsDrop={setParentHoveredChildAcceptsDrop}
        >
            {({ connectDropTarget, dropIndicator }) => (
                <div
                    ref={connectDropTarget}
                    className="TaskListEditorContainer"
                    onMouseDown={onMouseDown}
                    onClick={onClick}
                >
                    <SearchHighlightObserver editor={editor} />
                    <EditorContent
                        innerRef={innerRef}
                        className="TaskListEditor"
                        editor={editor}
                        dir="auto"
                        spellCheck={spellCheck}
                    />
                    {dropIndicator && <div className="TaskListDropIndicator" style={dropIndicator} />}
                </div>
            )}
        </TaskListDropTarget>
    );
};

export default TaskListEditor;
